import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ProductsProvider } from './contexts/ProductsContext';
import { CartProvider } from './contexts/CartContext';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import CartDrawer from './components/CartDrawer';

import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import CollectionPage from './pages/CollectionPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import TermsPage from './pages/TermsPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminReviewsPage from './pages/AdminReviewsPage';

import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F3EE]">
        <div className="w-8 h-8 border border-[#C6A978] border-t-transparent rounded-full animate-spin" />
      </div>
    );

  if (!user || user.role !== 'admin')
    return <Navigate to="/admin/login" replace />;

  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function Layout({ children, showFooter = true }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');

  return (
    <>
      <Header />
      {children}
      {showFooter && <Footer />}

      {!isAdmin && <FloatingWhatsApp />}
      {!isAdmin && <CartDrawer />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProductsProvider>
      <SettingsProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />

            <Routes>
              <Route path="/" element={<Layout><HomePage /></Layout>} />

              <Route path="/shop" element={<Layout><ShopPage /></Layout>} />

              <Route
                path="/collection/:slug"
                element={<Layout><CollectionPage /></Layout>}
              />

              <Route
                path="/product/:productId"
                element={<Layout><ProductDetailPage /></Layout>}
              />

              <Route path="/about" element={<Layout><AboutPage /></Layout>} />

              <Route path="/contact" element={<Layout><ContactPage /></Layout>} />

              <Route path="/faq" element={<Layout><FAQPage /></Layout>} />

              <Route
                path="/shipping"
                element={<Layout><ShippingPolicyPage /></Layout>}
              />

              <Route path="/terms" element={<Layout><TermsPage /></Layout>} />

              <Route
                path="/admin/login"
                element={
                  <Layout showFooter={false}>
                    <AdminLoginPage />
                  </Layout>
                }
              />

              <Route path="/admin/reviews" element={<AdminReviewsPage />} />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Layout showFooter={false}>
                      <AdminDashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute>
                    <Layout showFooter={false}>
                      <AdminSettingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </SettingsProvider>
      </ProductsProvider>
    </AuthProvider>
  );
}

export default App;